import clsx from 'clsx'
import { forwardRef, cloneElement, type ButtonHTMLAttributes, type ReactElement, type HTMLAttributes } from 'react'
import { CircularProgress, ThemeProvider, createTheme } from '@mui/material'
import { CheckCircle, XCircle } from '../../assets/icons/index.ts'
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES, ICON_BUTTON_COLORS } from './constants'
import type { IconButtonColor, IconButtonLoadingState, IconButtonSize } from './types'

const spinnerTheme = createTheme({
  palette: {
    primary: {
      main: '#fff',
    },
  },
})

// ============================================================================
// STYLE DEFINITIONS
// ============================================================================

/**
 * Style definitions per variant
 * Note: Only plain variant supports multiple colors
 */
const variantStyles = {
  // Filled: Blue background
  filled: clsx(
    'bg-brand-500 text-white shadow-sm',
    'hover:bg-brand-800 active:bg-brand-800',
    'focus-visible:outline-brand-500',
    'disabled:bg-true-gray-300 disabled:text-white',
    '[&[data-loading-state="true"]]:bg-brand-800 [&[data-loading-state="true"]]:text-white'
  ),
  // Outlined: Blue border
  outlined: clsx(
    'bg-transparent text-brand-500',
    'ring-1 ring-inset ring-brand-500',
    'hover:bg-brand-neutral-100 active:bg-brand-neutral-100',
    'focus-visible:outline-brand-500',
    'disabled:text-true-gray-300 disabled:ring-true-gray-300 disabled:bg-transparent',
    '[&[data-loading-state="true"]]:bg-neutral-100 [&[data-loading-state="true"]]:text-brand-600 [&[data-loading-state="true"]]:ring-brand-600'
  ),
  // Tonal: Neutral background
  tonal: clsx(
    'bg-brand-neutral-200 text-brand-neutral-800',
    'ring-1 ring-inset ring-brand-neutral-300',
    'hover:bg-brand-neutral-300 active:bg-brand-neutral-300',
    'focus-visible:outline-brand-neutral-500',
    'disabled:bg-true-gray-100 disabled:text-true-gray-300 disabled:ring-transparent',
    '[&[data-loading-state="true"]]:bg-brand-neutral-300 [&[data-loading-state="true"]]:text-brand-neutral-800 [&[data-loading-state="true"]]:ring-brand-neutral-500'
  ),
} as const

/**
 * Plain variant color styles (icon color, hover background, and focus outline)
 */
const plainColorStyles = {
  primary: clsx(
    'text-brand-500',
    'hover:bg-brand-100 active:bg-brand-100',
    'focus-visible:outline-brand-500',
    'disabled:text-true-gray-300 disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-brand-800'
  ),
  neutral: clsx(
    'text-brand-neutral-600',
    'hover:bg-brand-neutral-200 active:bg-brand-neutral-200',
    'focus-visible:outline-brand-neutral-500',
    'disabled:text-true-gray-300 disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-brand-neutral-950'
  ),
  black: clsx(
    'text-brand-neutral-950',
    'hover:bg-brand-neutral-300 active:bg-brand-neutral-300',
    'focus-visible:outline-brand-neutral-900',
    'disabled:text-true-gray-300 disabled:hover:bg-transparent',
    '[&[data-loading-state="true"]]:text-brand-neutral-950'
  ),
} as const

/**
 * Size styles for filled, outlined, and tonal variants
 */
const iconButtonSizeStyles: Record<IconButtonSize, string> = {
  xs: 'h-[24px] w-[24px] rounded p-1',
  sm: 'h-[28px] w-[28px] rounded-md p-1',
  md: 'h-[36px] w-[36px] rounded-md p-1.5',
  lg: 'h-[40px] w-[40px] rounded-md p-2',
}

/**
 * Wrapper sizes for plain icon buttons
 * Plain variant uses a wrapper div for consistent layout sizing
 * xs is not available for plain variant
 */
const plainIconOuterSizes: Record<Exclude<IconButtonSize, 'xs'>, string> = {
  sm: 'h-[28px] w-[28px]',
  md: 'h-[36px] w-[36px]',
  lg: 'h-[40px] w-[40px]',
}

/**
 * Icon dimensions per button size
 */
const iconSizeByButtonSize: Record<IconButtonSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
}

/**
 * Spinner pixel size per button size
 */
const spinnerSizeByButtonSize: Record<IconButtonSize, number> = {
  xs: 13,
  sm: 13,
  md: 16,
  lg: 18,
}

/**
 * Base styles applied to all icon buttons
 */
const baseStyles = clsx(
  'inline-flex items-center justify-center font-medium transition-colors duration-200',
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'disabled:cursor-not-allowed'
)

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Shared icon button props (used by all variants)
 * Note: aria-label is required per WCAG 4.1.2 - icon-only buttons must have an accessible name.
 */
interface SharedIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'children'> {
  /**
   * Accessible name for the button (required).
   * Icon-only buttons have no visible text, so an aria-label is required for screen reader users.
   */
  'aria-label': string
  /**
   * Icon to display (required)
   */
  icon: ReactElement
  /**
   * The size of the icon button
   * @default 'md'
   */
  size?: IconButtonSize
  /**
   * Loading
   * - true or 'loading': Shows spinner and disables button
   * - 'success': Shows checkmark icon
   * - 'error': Shows X icon
   * - false/undefined: Shows normal icon
   * @default false
   */
  loading?: IconButtonLoadingState
}

/**
 * Props for filled/outlined/tonal variants (no color prop)
 */
type FilledOutlinedTonalProps = SharedIconButtonProps & {
  variant?: 'filled' | 'outlined' | 'tonal'
  color?: never
  wrapperClassName?: never
}

/**
 * Props for plain variant (color optional, size excludes xs)
 * Plain renders a wrapper div around the button so the hover circle can sit inside a larger
 * layout footprint — wrapperClassName styles that outer div (for positioning, margins, etc.).
 */
type PlainProps = Omit<SharedIconButtonProps, 'size'> & {
  variant: 'plain'
  color?: IconButtonColor
  size?: Exclude<IconButtonSize, 'xs'>
  wrapperClassName?: string
}

/**
 * Props for default (no variant specified, defaults to filled)
 */
type DefaultProps = SharedIconButtonProps & {
  variant?: never
  color?: never
  wrapperClassName?: never
}

export type IconButtonProps = FilledOutlinedTonalProps | PlainProps | DefaultProps

/**
 * IconButton Component
 *
 * A square icon-only button component following the Paxton design system.
 *
 * Variants:
 * - filled: Blue background (default)
 * - outlined: Blue border with transparent background
 * - tonal: Neutral background
 * - plain: No background - supports color prop (primary, neutral, black)
 *
 * @example
 * // Primary action (filled)
 * <IconButton icon={<PlusIcon />} aria-label="Add" />
 *
 * @example
 * // Secondary action (outlined)
 * <IconButton icon={<EditIcon />} variant="outlined" aria-label="Edit" />
 *
 * @example
 * // Subtle action (tonal)
 * <IconButton icon={<SettingsIcon />} variant="tonal" aria-label="Settings" />
 *
 * @example
 * // Plain style with colors
 * <IconButton icon={<XIcon />} variant="plain" color="primary" aria-label="Close" />
 * <IconButton icon={<XIcon />} variant="plain" color="neutral" aria-label="Close" />
 * <IconButton icon={<XIcon />} variant="plain" color="black" aria-label="Close" />
 *
 * @example
 * // Different sizes (xs only available for filled/outlined/tonal)
 * <IconButton icon={<SettingsIcon />} size="xs" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="sm" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="md" aria-label="Settings" />
 * <IconButton icon={<SettingsIcon />} size="lg" aria-label="Settings" />
 *
 * @example
 * // Override icon size for special cases
 * <IconButton icon={<CustomIcon className="h-5 w-5" />} size="sm" aria-label="Custom" />
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, color, disabled, icon, loading = false, size = ICON_BUTTON_SIZES.MD, variant = ICON_BUTTON_VARIANTS.FILLED, wrapperClassName, ...props },
  ref
) {
  const isLoading = loading === true || loading === 'loading'
  const isSuccess = loading === 'success'
  const isError = loading === 'error'
  const isDisabled = disabled || isLoading

  // Add data attribute for loading/success/error states
  const hasLoadingState = isLoading || isSuccess || isError
  const buttonProps = {
    ...props,
    ...(hasLoadingState && { 'data-loading-state': 'true' }),
  }

  const iconSize = iconSizeByButtonSize[size]

  // Render loading/success/error icon
  const renderStateIcon = () => {
    if (isLoading) {
      const spinnerSize = spinnerSizeByButtonSize[size]

      return (
        <span className={clsx('inline-flex items-center justify-center text-current', iconSize)} aria-hidden>
          <ThemeProvider theme={spinnerTheme}>
            <CircularProgress size={spinnerSize} thickness={7} sx={{ color: 'currentColor' }} />
          </ThemeProvider>
        </span>
      )
    }

    if (isSuccess) return <CheckCircle className={iconSize} aria-hidden />

    if (isError) return <XCircle className={iconSize} aria-hidden />

    return null
  }

  // Apply size classes directly to the icon element
  const iconWithSize = loading
    ? renderStateIcon()
    : cloneElement(icon, {
        className: clsx(iconSize, icon.props.className),
        'aria-hidden': true,
      } as Pick<HTMLAttributes<HTMLElement>, 'className' | 'aria-hidden'>)

  // Only `plain` accepts a configurable color; other variants encode color into the variant itself.
  const dsAttributes = {
    'data-ds-component': 'IconButton',
    'data-ds-variant': variant,
    'data-ds-size': size,
    ...(variant === ICON_BUTTON_VARIANTS.PLAIN && {
      'data-ds-color': color || ICON_BUTTON_COLORS.PRIMARY,
    }),
  }

  // Plain variant uses a wrapper for layout + circular button
  if (variant === ICON_BUTTON_VARIANTS.PLAIN) {
    const plainColor = color || ICON_BUTTON_COLORS.PRIMARY
    const plainSize = size as Exclude<IconButtonSize, 'xs'>

    return (
      <div
        data-testid="icon-button-wrapper"
        {...dsAttributes}
        className={clsx(plainIconOuterSizes[plainSize], 'inline-flex items-center justify-center', wrapperClassName)}
      >
        <button ref={ref} className={clsx(baseStyles, plainColorStyles[plainColor], 'rounded-full p-1', className)} disabled={isDisabled} {...buttonProps}>
          {iconWithSize}
        </button>
      </div>
    )
  }

  // Other variants (filled, outlined, tonal) use single-layer structure
  return (
    <button
      ref={ref}
      {...dsAttributes}
      className={clsx(baseStyles, variantStyles[variant], iconButtonSizeStyles[size], className)}
      disabled={isDisabled}
      {...buttonProps}
    >
      {iconWithSize}
    </button>
  )
})

export { IconButton }
